import type { Except } from "type-fest";
import { z } from "zod";
import {
    hasProviderDestination,
    providersWithSubDomain,
} from "../destinations/destinations";
import type { ProductId} from "../products/product-id";
import { productIdZodSchema } from "../products/product-id";
import type {
    ProviderGroupId} from "../provider-groups/provider-group-id";
import {
    providerGroupIdZodSchema,
} from "../provider-groups/provider-group-id";
import type { Provider } from "../providers/provider";
import type { ProviderId} from "../providers/provider-id";
import { providerIdZodSchema } from "../providers/provider-id";
import type {
    HexColor} from "../stylable/hex-color/hex-color";
import {
    hexColor,
    hexColorZodSchema,
} from "../stylable/hex-color/hex-color";
import type { PromoId} from "./promo-id.model";
import { promoIdFactory, promoIdZodSchema } from "./promo-id.model";

type Promo = {
    _id: PromoId;
    _type: "PROMO";
    ownerId: ProviderId | ProviderGroupId;
    /**
     * e.g. https://camping.towni.se \
     * defaults to https://towni.se \
     * @type {string}
     */
    endpoint: string;
    // todo! host should be moved to some kind of provider
    // todo! group data
    title: string;
    subtitle: string;
    relativeUrl?: string;
    /**
     * If not provided it falls back to be the same as the endpoint
     * @type {string}
     */
    storefrontUrl?: string;
    actionText: string;
    productIds: ProductId[];
    actionColors: {
        color1: HexColor;
        color2?: HexColor;
        textColor: HexColor;
    };
    colors: {
        color1: HexColor;
        color2?: HexColor;
        textColor: HexColor;
    };
};

type EmptyPromo = Except<Promo, "ownerId" | "_type" | "_id"> & {
    _id: "new";
    _type: "EMPTY_PROMO";
    ownerId?: ProviderId | ProviderGroupId;
};
const emptyPromoFactory = (host: string): EmptyPromo => ({
    _id: "new",
    _type: "EMPTY_PROMO",
    actionColors: {
        color1: hexColor("#fddb5b"),
        color2: hexColor("#ff991e"),
        textColor: hexColor("#ffffff"),
    },
    actionText: "Upptäck vårt utbud",
    endpoint: z.string().url().parse(host), // TODO! implement support for destion domains as well
    title: "",
    subtitle: "",
    productIds: [],
    colors: {
        color1: hexColor("#0f9dd1"),
        color2: hexColor("#1a5080"),
        textColor: hexColor("#ffffff"),
    },
});

const toPromoFromEmptyPromo = (
    emptyPromo: EmptyPromo | Promo,
    provider: Provider,
    endpoint: string,
): Promo => {
    if (emptyPromo._type === "PROMO") return emptyPromo;
    const { ownerId, _type, ...promo } = emptyPromo;
    if (!ownerId) throw new Error("missing ownerId");
    if (!endpoint.trim()) throw new Error("endpoint must be set");
    const relativeUrl =
        emptyPromo.relativeUrl?.trim() ||
        !!providersWithSubDomain[provider.slug]
            ? undefined
            : hasProviderDestination[provider.slug]
              ? `/destinations/${provider.slug}`
              : `/shop/${provider.slug}`;
    const storefrontUrl = z
        .string()
        .url()
        .parse(
            emptyPromo.storefrontUrl?.trim() ??
                `${endpoint}${relativeUrl ?? ""}`,
        );
    return {
        ...promo,
        _type: "PROMO",
        ownerId,
        _id: promoIdFactory(),
        endpoint,
        relativeUrl,
        storefrontUrl,
    };
};

const promoZodSchema: z.ZodSchema<Promo> = z.object({
    _id: promoIdZodSchema,
    _type: z.literal("PROMO"),
    ownerId: providerIdZodSchema.or(providerGroupIdZodSchema),
    endpoint: z.string().url(),
    title: z.string(),
    subtitle: z.string(),
    productIds: z.array(productIdZodSchema),
    storefrontUrl: z.string().url().optional(),
    relativeUrl: z.string().optional(),
    actionText: z.string(),
    actionColors: z.object({
        color1: hexColorZodSchema,
        color2: hexColorZodSchema,
        textColor: hexColorZodSchema,
    }),
    colors: z.object({
        color1: hexColorZodSchema,
        color2: hexColorZodSchema,
        textColor: hexColorZodSchema,
    }),
});

export { emptyPromoFactory, promoZodSchema, toPromoFromEmptyPromo };
export type { EmptyPromo, Promo };
